import { Injectable } from '@angular/core';
import moment from 'moment';
import { Warehouse } from '../models/warehouse.model';
import { ProductStockLevel } from '../models/product-stock-level.model';
import { ProductStockMovement, MOVEMENT_TYPE_LABELS } from '../models/product-stock-movement.model';
import { ChartSeries, ChartGroup, WarehousingChart } from '../models/warehousing-chart.model';
import { StockQuantityService } from './stock-quantity.service';

/**
 * Categorical palette for the warehousing charts, in fixed slot order.
 *
 * This is NOT the palette the three existing home-page graphs use. That one
 * (`#5AA454, #E44D25, ...`) is copy-pasted into each partial with no shared constant and
 * it fails a colourblind check: `#E44D25` and `#5AA454` separate by only ΔE 5.1 under
 * deuteranopia, which is the red/green pair, near-identical for roughly one man in twelve.
 * On a screen where red means stock has been lost, that is not good enough.
 *
 * This palette was validated rather than eyeballed — lightness band, chroma floor,
 * colourblind separation and normal-vision separation all pass. Contrast against a white
 * card came back as a warning, which obliges relief: every chart here keeps a readable
 * table or direct labels beside it, so no value is carried by hue alone.
 */
export const WAREHOUSING_CHART_PALETTE: string[] = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300'
];

/**
 * Status colours are reserved and must never appear as a series hue, otherwise "red"
 * stops meaning "wrong" and starts meaning "the second warehouse".
 */
export const STATUS_CRITICAL: string = '#d03b3b';
export const STATUS_GOOD: string = '#0ca30c';
export const STATUS_WARNING: string = '#fab219';

/** Past this, extra bars become extra hues for no information. The tail folds into Other. */
const TOP_PRODUCT_LIMIT: number = 8;

/**
 * How many warehouses get their own hue.
 *
 * Five rather than the palette's six, so that five named sites plus the Other bucket is
 * six pie segments — the most a part-to-whole stays readable at. A further site must not
 * wrap back onto slot one: two sites sharing a colour is worse than an honest Other bucket,
 * because the reader has no way to know it happened.
 */
const MAX_SITE_HUES: number = 5;

/** The fold-in bucket. Deliberately a neutral, so it never reads as a site in its own right. */
const OTHER_LABEL: string = 'Other sites';
const OTHER_COLOR: string = '#8a8886';

/** A pie needs enough slices to be a part-to-whole picture rather than a decorated number. */
const MIN_PIE_SEGMENTS: number = 3;

/** One bar is not a trend. Below this the time chart is suppressed. */
const MIN_TIME_BUCKETS: number = 2;

@Injectable({
  providedIn: 'root'
})
export class WarehousingChartService {

  constructor(private stockQuantityService: StockQuantityService) { }

  /**
   * Stock share by warehouse — a genuine part-to-whole, so a pie is legitimate, but only
   * above a floor. A two-slice pie is a decorated pair of numbers, and with one warehouse
   * it is a filled circle, so both fall back to the tiles the dashboard already shows.
   *
   * Negative levels are excluded rather than plotted: a negative slice of a whole is
   * meaningless. They are not hidden — the dashboard's negative callout is the place that
   * reports them, and this method flags that it happened.
   *
   * @param levels every stock level, joined to location and warehouse
   * @param warehouses all warehouses, used to pin a stable hue per site
   */
  stockShareByWarehouse(levels: ProductStockLevel[], warehouses: Warehouse[]): WarehousingChart {
    const chart: WarehousingChart = new WarehousingChart();

    const totals: any = {};

    levels.forEach((level: ProductStockLevel) => {
      const raw: string = this.warehouseNameOf(level);

      if (!raw) {
        return;
      }

      // Folded through the same bucketing as the stacked bar, so a site is the same colour
      // and the same slice in both, and a seventh site never reuses another's hue.
      const name: string = this.siteLabelOf(raw, warehouses);

      totals[name] = (totals[name] || 0) + Number(level.qty);
    });

    // Only positive shares can be part of a whole.
    const positive: ChartSeries[] = Object.keys(totals)
      .filter(name => this.stockQuantityService.isPositive(totals[name]))
      .map(name => ({
        name: name,
        value: this.stockQuantityService.round(totals[name])
      } as ChartSeries));

    const negativeSites: number = Object.keys(totals)
      .filter(name => this.stockQuantityService.isNegative(totals[name])).length;

    chart.data = positive;
    chart.customColors = this.stableWarehouseColors(warehouses);
    chart.labelMap = this.siteLabelMap(warehouses);

    if (positive.length < MIN_PIE_SEGMENTS) {
      chart.renderable = false;
      chart.suppressedBecause = positive.length === 0
        ? 'No warehouse is currently holding stock.'
        : 'Only ' + positive.length + ' warehouse'
          + (positive.length === 1 ? '' : 's')
          + ' hold stock, so the totals below say it more clearly than a chart would.';
      return chart;
    }

    chart.renderable = true;

    if (negativeSites > 0) {
      chart.suppressedBecause = negativeSites + ' warehouse'
        + (negativeSites === 1 ? ' is' : 's are')
        + ' at a negative total and cannot be shown as a share — see the alert above.';
    }

    return chart;
  }

  /**
   * Top products by quantity held — one series, so one colour for every bar.
   *
   * Deliberately not shaded darker-where-bigger: that would encode size as hue when the
   * bar length already says it, and products have no natural order to justify a ramp.
   * Horizontal because product names are long enough to collide when rotated.
   */
  topProductsByQty(levels: ProductStockLevel[]): WarehousingChart {
    const chart: WarehousingChart = new WarehousingChart();

    const totals: any = {};

    levels.forEach((level: ProductStockLevel) => {
      const name: string = level.product && level.product.name ? level.product.name : 'Unknown product';

      totals[name] = (totals[name] || 0) + Number(level.qty);
    });

    // A level at zero is a real fact about a shelf, but a zero-length bar says nothing, so
    // only products that actually hold something (or owe something) are plotted.
    let rows: ChartSeries[] = Object.keys(totals)
      .filter(name => !this.stockQuantityService.isZero(totals[name]))
      .map(name => ({
        name: name,
        value: this.stockQuantityService.round(totals[name])
      } as ChartSeries));

    // Sort by magnitude so a large negative is as visible as a large positive.
    rows.sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

    if (rows.length > TOP_PRODUCT_LIMIT) {
      const tail: ChartSeries[] = rows.slice(TOP_PRODUCT_LIMIT);
      const other: number = tail.reduce((sum: number, row: ChartSeries) => sum + row.value, 0);

      rows = rows.slice(0, TOP_PRODUCT_LIMIT);
      rows.push({ name: 'Other (' + tail.length + ')', value: this.stockQuantityService.round(other) } as ChartSeries);
    }

    chart.data = rows;

    // One series means one hue for the lot, so every bar is pinned to slot 1.
    chart.customColors = rows.map((row: ChartSeries) => ({
      name: row.name,
      value: this.stockQuantityService.isNegative(row.value) ? STATUS_CRITICAL : WAREHOUSING_CHART_PALETTE[0]
    }));

    chart.renderable = rows.length > 0;
    chart.suppressedBecause = rows.length > 0 ? '' : 'No product is currently holding stock.';

    return chart;
  }

  /**
   * Movements by type, stacked by warehouse. Two real categorical dimensions, which is what
   * earns the stack — counting rows rather than summing qty, because a transfer's matched
   * pair would cancel to zero and read as "nothing happened".
   */
  movementsByType(movements: ProductStockMovement[], warehouses: Warehouse[]): WarehousingChart {
    const chart: WarehousingChart = new WarehousingChart();

    const byType: any = {};

    movements.forEach((movement: ProductStockMovement) => {
      const type: string = this.movementTypeLabel(movement.movementType);
      const site: string = this.siteLabelOf(this.warehouseNameOfMovement(movement), warehouses);

      if (!byType[type]) {
        byType[type] = {};
      }

      byType[type][site] = (byType[type][site] || 0) + 1;
    });

    // Every group carries every site, including zeros, so the stacks stay comparable and a
    // site does not change colour between one bar and the next.
    const siteNames: string[] = this.siteLabels(warehouses);

    chart.data = Object.keys(byType).map((type: string) => {
      const present: string[] = Object.keys(byType[type]);
      const names: string[] = siteNames.slice();

      // A movement whose warehouse join is missing would otherwise vanish from the chart.
      present.forEach(name => {
        if (names.indexOf(name) === -1) {
          names.push(name);
        }
      });

      return {
        name: type,
        series: names.map((site: string) => ({
          name: site,
          value: byType[type][site] || 0
        } as ChartSeries))
      } as ChartGroup;
    });

    chart.customColors = this.stableWarehouseColors(warehouses);
    chart.labelMap = this.siteLabelMap(warehouses);
    chart.renderable = chart.data.length > 0;
    chart.suppressedBecause = chart.data.length > 0 ? '' : 'No stock has been moved yet.';

    return chart;
  }

  /**
   * Movement volume per day. Suppressed below two buckets, because a single bar is a number
   * pretending to be a trend — the "Movements Today" tile already says it better.
   */
  movementsByDay(movements: ProductStockMovement[]): WarehousingChart {
    const chart: WarehousingChart = new WarehousingChart();

    const byDay: any = {};

    movements.forEach((movement: ProductStockMovement) => {
      if (!movement.createdAt) {
        return;
      }

      const day: string = moment(movement.createdAt).format('YYYY-MM-DD');

      byDay[day] = (byDay[day] || 0) + 1;
    });

    const days: string[] = Object.keys(byDay).sort();

    chart.data = days.map((day: string) => ({
      name: moment(day).format('DD/MM'),
      value: byDay[day]
    } as ChartSeries));

    // Single series, so a single hue and no legend.
    chart.customColors = chart.data.map((row: any) => ({
      name: row.name,
      value: WAREHOUSING_CHART_PALETTE[0]
    }));

    chart.renderable = days.length >= MIN_TIME_BUCKETS;
    chart.suppressedBecause = chart.renderable
      ? ''
      : (days.length === 0
        ? 'No stock has been moved yet.'
        : 'Every movement so far falls on one day, so there is no trend to plot yet.');

    return chart;
  }

  /**
   * Hue per warehouse, derived from the warehouse id rather than from its position in
   * whatever list is being drawn. That is what keeps a site the same colour across the pie
   * and the stacked bar, and across a filter that removes one of them.
   *
   * Only the first MAX_SITE_HUES sites get a hue. The rest are not given a wrapped-around
   * colour — they are folded into one neutral bucket by `siteLabelOf`, so no two sites ever
   * share a hue without the chart saying so.
   */
  private stableWarehouseColors(warehouses: Warehouse[]): any[] {
    // Keyed on the charted label, which is what ngx-charts matches against.
    const colors: any[] = this.namedSites(warehouses).map((warehouse: Warehouse, index: number) => ({
      name: this.chartLabelOf(warehouse.name, warehouses),
      value: WAREHOUSING_CHART_PALETTE[index]
    }));

    if (warehouses.length > MAX_SITE_HUES) {
      colors.push({ name: OTHER_LABEL, value: OTHER_COLOR });
    }

    // A movement whose warehouse join came back absent is charted rather than dropped, and
    // gets the neutral so it never looks like one of the named sites.
    colors.push({ name: 'Unknown warehouse', value: OTHER_COLOR });

    return colors;
  }

  /** The sites that get their own hue, in a stable order that does not depend on the data. */
  private namedSites(warehouses: Warehouse[]): Warehouse[] {
    return warehouses.slice().sort((a, b) => a.id - b.id).slice(0, MAX_SITE_HUES);
  }

  /**
   * The label a site is plotted under, with any prefix shared by every site removed.
   *
   * ngx-charts trims legend labels to 10 characters and offers no input to widen it, so
   * "Blacks Main Depot" and "Blacks Secondary Depot" both render as "Blacks ..." — two
   * identical legend entries for two different sites. Dropping the common prefix is what
   * makes the legend say anything at all. The full name survives in `labelMap` for the
   * table twin, so nothing is actually lost.
   */
  private chartLabelOf(name: string, warehouses: Warehouse[]): string {
    const prefix: string = this.commonSitePrefix(warehouses);

    if (prefix && name.indexOf(prefix) === 0 && name.length > prefix.length) {
      return name.substring(prefix.length);
    }

    return name;
  }

  /**
   * The longest run of whole leading words shared by every warehouse name.
   *
   * Whole words only — clipping mid-word would turn "Northgate" and "Northampton" into
   * "gate" and "ampton". Returns '' unless there are at least two sites and every trimmed
   * name keeps enough characters to stay meaningful.
   */
  private commonSitePrefix(warehouses: Warehouse[]): string {
    const names: string[] = warehouses.map(w => w.name).filter(n => !!n);

    if (names.length < 2) {
      return '';
    }

    const firstWords: string[] = names[0].split(' ');
    let prefix: string = '';

    for (let take = 1; take < firstWords.length; take++) {
      const candidate: string = firstWords.slice(0, take).join(' ') + ' ';

      const sharedByAll: boolean = names.every(n => n.indexOf(candidate) === 0);
      // Every remaining name must still carry something worth reading.
      const leavesEnough: boolean = names.every(n => n.length - candidate.length >= 3);

      if (sharedByAll && leavesEnough) {
        prefix = candidate;
      } else {
        break;
      }
    }

    return prefix;
  }

  /** Charted label -> full name, so a table twin can show what the legend had to shorten. */
  private siteLabelMap(warehouses: Warehouse[]): any {
    const map: any = {};

    warehouses.forEach((warehouse: Warehouse) => {
      map[this.chartLabelOf(warehouse.name, warehouses)] = warehouse.name;
    });

    map[OTHER_LABEL] = OTHER_LABEL;

    return map;
  }

  /**
   * The label a site is charted under — itself, or the Other bucket once the palette is
   * exhausted. Both charts route every site name through here so the buckets agree.
   */
  private siteLabelOf(name: string, warehouses: Warehouse[]): string {
    if (!name) {
      return 'Unknown warehouse';
    }

    if (warehouses.length <= MAX_SITE_HUES) {
      return this.chartLabelOf(name, warehouses);
    }

    return this.namedSites(warehouses).filter(w => w.name === name).length > 0
      ? this.chartLabelOf(name, warehouses)
      : OTHER_LABEL;
  }

  /** The site labels a chart may show, in hue order, with Other last if it is in play. */
  private siteLabels(warehouses: Warehouse[]): string[] {
    const labels: string[] = this.namedSites(warehouses).map(w => this.chartLabelOf(w.name, warehouses));

    if (warehouses.length > MAX_SITE_HUES) {
      labels.push(OTHER_LABEL);
    }

    return labels;
  }

  private warehouseNameOf(level: ProductStockLevel): string {
    return level.warehouseLocation && level.warehouseLocation.warehouse
      ? level.warehouseLocation.warehouse.name : '';
  }

  private warehouseNameOfMovement(movement: ProductStockMovement): string {
    return movement.warehouseLocation && movement.warehouseLocation.warehouse
      ? movement.warehouseLocation.warehouse.name : '';
  }

  private movementTypeLabel(movementType: string): string {
    const type: any = MOVEMENT_TYPE_LABELS.filter(t => t.value === movementType)[0];

    return type ? type.label : movementType;
  }
}
